import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import ExternalLink from "../../../assets/images/clinical/ExternalLink.svg"
import { useAppDispatch } from "../../../core/app/hooks"
import {
  fetchIfuDoc,
  fetchReleaseNotes,
} from "../../../core/app/slices/user/userApis"
import { UText } from "../../../ui-component"
import { Box, Divider, Link } from "../../../ui-component/mui.components"
import { convertBlobToPdfUrl } from "../../Patient/PatientList/util/commonUtil"
import { UDesignCloudAppMinimumRequirements } from "@/core/constants/MinSystemRequirements"

const ReleaseNotes: FC = () => {
  const { t } = useTranslation("common")
  const [releaseNotes, setReleaseNotes] = useState([])
  const dispatch = useAppDispatch()

  useEffect(() => {
    const notes = []
    const releasesCount = Object.keys(
      t("releaseNotes.releaseNotes", { returnObjects: true }),
    ).filter((key) => key.startsWith("release")).length

    for (let i = releasesCount-1; i >= 0; i--) {
      notes.push({
        date: t(`releaseNotes.releaseNotes.release${i}.date`),
        version: t(`releaseNotes.releaseNotes.release${i}.version`),
        versionKey: t(`releaseNotes.releaseNotes.release${i}.version`).split(' ').reverse()[0]
      })
    }
    setReleaseNotes(notes)
  }, [])

  const downLoadIFU = () => {
    dispatch(fetchIfuDoc()).then((res: any) => {
      const link = document.createElement("a")
      link.href = convertBlobToPdfUrl(res.payload)
      // Open the downloaded PDF in a new tab
      window.open(link.href, "_blank")
    })
  }

  const getReleaseNotes = (versionKey: string) => {
    dispatch(fetchReleaseNotes(versionKey)).then((res: any) => {
      const link = document.createElement("a")
      link.href = convertBlobToPdfUrl(res.payload)
      // Open the downloaded PDF in a new tab
      window.open(link.href, "_blank")
    })
  }

  return (
    <Box
      data-testid={"releaseNotes"}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          paddingY: 2,
        }}
      >
        <UText variant={"h4"}>{t("releaseNotes.title")}</UText>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          boxShadow: 1,
          padding: "40px 80px",
          width: "79.6%",
          alignSelf: "center",
          backgroundColor: "background.default",
          borderRadius: 2,
          gap: 0.5,
          marginBottom: 6,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 4,
            marginBottom: "28px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flexBasis: "65%",
            }}
          >
            <UText
              sxProp={{
                marginTop: "20px",
              }}
              color={"text.primary"}
              variant={"h5"}
            >
              {t("releaseNotes.recommendedDisplay.title")}
            </UText>
            <UText
              sxProp={{
                color: "#000",
              }}
              variant={"body1"}
            >
              {t("releaseNotes.recommendedDisplay.content")}
            </UText>
            <UText
              sxProp={{
                marginTop: "20px",
              }}
              color={"text.primary"}
              variant={"h5"}
            >
              {UDesignCloudAppMinimumRequirements.title}
            </UText>
              <Box component="ol" sx={{ 
                pl: 2, // padding left to make room for numbers
                m: 0,  // remove default margin
                '& > li': {
                  mb: 1, // margin bottom between list items
                  pl: 1, // padding left for list item content
                }
              }}>
                {UDesignCloudAppMinimumRequirements.requirements.map((requirement, index) => (
                  <Box component="li" key={`requirement-${index}`}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <UText variant="body1" sxProp={{ fontWeight: 500 }}>
                        {requirement.label}:
                      </UText>
                      {requirement.content ? (
                        <UText variant="body1" sxProp={{ mt: 0.5 }}>
                          {requirement.content}
                        </UText>
                      ) : requirement.subItems ? (
                        <Box component="ul" sx={{ 
                          listStyle: 'disc', // Use disc for rounded dots
                          pl: 2,
                          mt: 1,
                          '& > li': {
                            mb: 1.5,
                            '&::marker': { // Style the bullet point
                              color: 'text.secondary',
                              fontSize: '1.2em'
                            }
                          }
                        }}>
                          {requirement.subItems.map((subItem, subIndex) => (
                            <Box component="li" key={`subitem-${subIndex}`}>
                              <UText variant="body1" sxProp={{ fontWeight: 500 }}>
                                {subItem.label}:
                              </UText>
                              {Array.isArray(subItem.content) ? (
                                <Box component="ul" sx={{ 
                                  listStyle: 'disc', // Use disc for rounded dots
                                  pl: 2,
                                  mt: 0.5,
                                  '& > li': {
                                    mb: 0.5,
                                    '&::marker': { // Style the bullet point
                                      color: 'text.secondary',
                                      fontSize: '1em'
                                    }
                                  }
                                }}>
                                  {subItem.content.map((line, lineIndex) => (
                                    <Box component="li" key={`line-${lineIndex}`}>
                                      <UText variant="body1">
                                        {line}
                                      </UText>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <UText variant="body1" sxProp={{ mt: 0.5 }}>
                                  {subItem.content}
                                </UText>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flexBasis: "35%",
            }}
          >
            <UText
              sxProp={{
                marginTop: "20px",
              }}
              color={"text.primary"}
              variant={"h5"}
            >
              {t("releaseNotes.instructionsForUse.title")}
            </UText>
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Link  href="https://www.ulabsystems.com/ifu/" target="_blank" rel="noopener noreferrer" sx={{
                display: "flex",
                gap: 1,
              }}>
                <UText
                  variant={"body1"}
                  color={"primary.main"}
                  sxProp={{
                    textDecoration: "underline",
                    display: "flex",
                    fontWeight: 500,
                  }}
                >
                  {t("releaseNotes.instructionsForUse.link")}
                </UText>
                <Box
                  component={"img"}
                  sx={{
                    color: "text.secondary",
                  }}
                  src={ExternalLink}
                  alt={"External Link"}
                />
              </Link>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <UText
            sxProp={{
              display: "flex",
              marginTop: "20px",
            }}
            color={"text.primary"}
            variant={"h5"}
          >
            {t("releaseNotes.releaseNotes.title")}
          </UText>
          {releaseNotes?.map((note, index) => {
            return (
              <Box
                key={"releaseNotes-" + index}
                sx={{
                  display: "flex",
                  gap: 3,
                  paddingY: 1,
                }}
              >
                <UText
                  sxProp={{
                    flexBasis: "20.2%",
                  }}
                  variant={"body1"}
                >
                  {note.date}
                </UText>
                <Link
                  onClick={() => getReleaseNotes(note.versionKey)}
                  sx={{
                    cursor: "pointer",
                    flexBasis: "79.8%",
                    color: "primary.main",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  {/* <UText
                    sxProp={{
                      flexBasis: "79.8%",
                      color: "primary.main",
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                    variant={"body1"}
                  > */}
                  {note.version}
                  {/* </UText> */}
                </Link>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default ReleaseNotes
